/**
 * SiteSettingsScreen — coordinator edits the singleton site settings
 * (Stage 3.3g). Covers identity, bank details, the donation appeal, contact,
 * and social links. Saves via the coordinator-only updateSiteSettings mutation
 * (audit-logged server-side).
 */
import { Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "urql";

import { cn } from "../lib/cn";
import { showError, showSuccess } from "../lib/sweet";

const SETTINGS_QUERY = /* GraphQL */ `
  query AdminSiteSettings {
    siteSettings {
      siteTitle siteTitleKu siteTitleAr
      tagline taglineKu taglineAr
      bankAccountName bankName bankSortCode bankAccountNumber
      paymentInstructions paymentInstructionsKu paymentInstructionsAr
      donationEnabled
      donationHeading donationHeadingKu donationHeadingAr
      donationMessage donationMessageKu donationMessageAr
      donationReference
      contactEmail contactPhone
      contactAddress contactAddressKu contactAddressAr
      facebookUrl instagramUrl youtubeUrl xUrl tiktokUrl
    }
  }
`;

const UPDATE_SETTINGS = /* GraphQL */ `
  mutation UpdateSiteSettings($input: UpdateSiteSettingsInput!) {
    updateSiteSettings(input: $input) { siteTitle }
  }
`;

type TextKey =
  | "siteTitle" | "siteTitleKu" | "siteTitleAr"
  | "tagline" | "taglineKu" | "taglineAr"
  | "bankAccountName" | "bankName" | "bankSortCode" | "bankAccountNumber"
  | "paymentInstructions" | "paymentInstructionsKu" | "paymentInstructionsAr"
  | "donationHeading" | "donationHeadingKu" | "donationHeadingAr"
  | "donationMessage" | "donationMessageKu" | "donationMessageAr"
  | "donationReference"
  | "contactEmail" | "contactPhone"
  | "contactAddress" | "contactAddressKu" | "contactAddressAr"
  | "facebookUrl" | "instagramUrl" | "youtubeUrl" | "xUrl" | "tiktokUrl";

type Form = Record<TextKey, string> & { donationEnabled: boolean };

const TEXT_KEYS: TextKey[] = [
  "siteTitle", "siteTitleKu", "siteTitleAr",
  "tagline", "taglineKu", "taglineAr",
  "bankAccountName", "bankName", "bankSortCode", "bankAccountNumber",
  "paymentInstructions", "paymentInstructionsKu", "paymentInstructionsAr",
  "donationHeading", "donationHeadingKu", "donationHeadingAr",
  "donationMessage", "donationMessageKu", "donationMessageAr",
  "donationReference",
  "contactEmail", "contactPhone",
  "contactAddress", "contactAddressKu", "contactAddressAr",
  "facebookUrl", "instagramUrl", "youtubeUrl", "xUrl", "tiktokUrl",
];

function emptyForm(): Form {
  const f = { donationEnabled: false } as Form;
  for (const k of TEXT_KEYS) f[k] = "";
  return f;
}

export function SiteSettingsScreen() {
  const [{ data, fetching, error }] = useQuery<{ siteSettings: Partial<Form> }>({
    query: SETTINGS_QUERY,
    requestPolicy: "cache-and-network",
  });
  const [, updateSettings] = useMutation(UPDATE_SETTINGS);

  const [form, setForm] = useState<Form>(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const s = data?.siteSettings;
    if (!s) return;
    const next = emptyForm();
    for (const k of TEXT_KEYS) next[k] = (s[k] as string | null) ?? "";
    next.donationEnabled = Boolean(s.donationEnabled);
    setForm(next);
  }, [data]);

  function set(key: TextKey, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const res = await updateSettings({ input: { ...form } });
      if (res.error) {
        await showError({ title: "Couldn't save", text: res.error.message });
        return;
      }
      showSuccess({ title: "Settings saved" });
    } finally {
      setSaving(false);
    }
  }

  if (fetching && !data) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-12 text-center text-stone-500">
        <Loader2 className="mx-auto size-5 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-12">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Site settings</h1>
          <p className="mt-1 text-sm text-stone-500">
            Bank details, the donation appeal, contact info and social links.
          </p>
        </div>
        <SaveButton saving={saving} onClick={handleSave} />
      </header>

      {/* Donations */}
      <Section title="Donations" hint="Shown as the appeal under the banner on the home page.">
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={form.donationEnabled}
            onChange={(e) => setForm((f) => ({ ...f, donationEnabled: e.target.checked }))}
            className="size-4 accent-[var(--color-flag-green)]"
          />
          Show the donation appeal on the home page
        </label>
        <Tri label="Heading" k="donationHeading" form={form} set={set} placeholder="e.g. Support our community" />
        <Tri label="Message" k="donationMessage" form={form} set={set} textarea />
        <Field label="Reference (optional)" k="donationReference" form={form} set={set} placeholder="e.g. DONATION" />
      </Section>

      {/* Bank details */}
      <Section title="Bank details" hint="Used by the donation appeal and the membership signup page.">
        <Field label="Account name" k="bankAccountName" form={form} set={set} />
        <Field label="Bank" k="bankName" form={form} set={set} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Sort code" k="bankSortCode" form={form} set={set} placeholder="08-92-99" />
          <Field label="Account number" k="bankAccountNumber" form={form} set={set} placeholder="12345678" />
        </div>
      </Section>

      {/* Identity */}
      <Section title="Site identity">
        <Tri label="Site title" k="siteTitle" form={form} set={set} />
        <Tri label="Tagline" k="tagline" form={form} set={set} />
      </Section>

      {/* Contact */}
      <Section title="Contact (footer)">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Email" k="contactEmail" form={form} set={set} />
          <Field label="Phone" k="contactPhone" form={form} set={set} />
        </div>
        <Tri label="Address" k="contactAddress" form={form} set={set} textarea />
      </Section>

      {/* Social */}
      <Section title="Social links (footer)">
        <Field label="Facebook URL" k="facebookUrl" form={form} set={set} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Instagram URL" k="instagramUrl" form={form} set={set} />
          <Field label="YouTube URL" k="youtubeUrl" form={form} set={set} />
          <Field label="X (Twitter) URL" k="xUrl" form={form} set={set} />
          <Field label="TikTok URL" k="tiktokUrl" form={form} set={set} />
        </div>
      </Section>

      <div className="flex justify-end">
        <SaveButton saving={saving} onClick={handleSave} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

const inputClass =
  "mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

function SaveButton({ saving, onClick }: { saving: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={saving}
      className="inline-flex items-center gap-2 rounded-md bg-flag-red px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
    >
      {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
      {saving ? "Saving…" : "Save changes"}
    </button>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-stone-200 bg-white p-4">
      <h2 className="text-xs font-medium uppercase tracking-wide text-stone-500">{title}</h2>
      {hint && <p className="mb-3 mt-0.5 text-xs text-stone-400">{hint}</p>}
      <div className={cn("space-y-3", !hint && "mt-3")}>{children}</div>
    </section>
  );
}

function Field({
  label,
  k,
  form,
  set,
  placeholder,
  textarea,
}: {
  label: string;
  k: TextKey;
  form: Form;
  set: (k: TextKey, v: string) => void;
  placeholder?: string;
  textarea?: boolean;
}) {
  return (
    <label className="block text-xs font-medium text-stone-600">
      {label}
      {textarea ? (
        <textarea rows={3} className={inputClass} value={form[k]} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} />
      ) : (
        <input type="text" className={inputClass} value={form[k]} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} />
      )}
    </label>
  );
}

/** Trilingual field — English / Kurmancî / Arabic inputs side by side. */
function Tri({
  label,
  k,
  form,
  set,
  placeholder,
  textarea,
}: {
  label: string;
  k: TextKey;
  form: Form;
  set: (k: TextKey, v: string) => void;
  placeholder?: string;
  textarea?: boolean;
}) {
  const ku = `${k}Ku` as TextKey;
  const ar = `${k}Ar` as TextKey;
  return (
    <div>
      <p className="text-xs font-medium text-stone-600">{label}</p>
      <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Cell k={k} form={form} set={set} hint="English" placeholder={placeholder} textarea={textarea} />
        <Cell k={ku} form={form} set={set} hint="Kurmancî" textarea={textarea} />
        <Cell k={ar} form={form} set={set} hint="العربية" dir="rtl" textarea={textarea} />
      </div>
    </div>
  );
}

function Cell({
  k,
  form,
  set,
  hint,
  placeholder,
  dir,
  textarea,
}: {
  k: TextKey;
  form: Form;
  set: (k: TextKey, v: string) => void;
  hint: string;
  placeholder?: string;
  dir?: "rtl";
  textarea?: boolean;
}) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-wide text-stone-400">{hint}</span>
      {textarea ? (
        <textarea rows={3} dir={dir} className={cn(inputClass, "mt-0.5")} value={form[k]} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} />
      ) : (
        <input type="text" dir={dir} className={cn(inputClass, "mt-0.5")} value={form[k]} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} />
      )}
    </div>
  );
}
